import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WindowManagerProvider as Provider } from '@/shared/lib/windows';
import type { OpenWindow, WindowRequest } from '@/shared/lib/windows';
import { DocumentWindow } from '@/widgets/document-window';
import { CategoryWindow } from '@/widgets/category-window';
import { CategoryGroupWindow } from '@/widgets/category-group-window';
import { CategoryCreateWindow } from '@/widgets/category-create-window';
import { RootGroupCreateWindow } from '@/widgets/root-group-create-window';
import { CounterpartyCreateWindow } from '@/widgets/counterparty-create-window';
import { PaymentWindow } from '@/widgets/payment-window';
import { OrderRequestWindow } from '@/widgets/order-request-window';
import { useLazyGetDocumentQuery } from '@/entities/document';

let seq = 0;

function keyFor(request: WindowRequest): string {
  if (request.kind === 'category') return request.id ? `category:${request.id}` : `category:new:${(seq += 1)}`;
  if (request.kind === 'categoryGroup') return `categoryGroup:${request.id}`;
  if (request.kind === 'categoryCreate') return `categoryCreate:${request.parentId}`;
  if (request.kind === 'rootGroupCreate') return 'rootGroupCreate';
  if (request.kind === 'counterpartyCreate') return 'counterpartyCreate';
  if (request.kind === 'payment') return request.id ? `payment:${request.id}` : `payment:new:${(seq += 1)}`;
  if (request.kind === 'orderRequest') return `orderRequest:${request.id}`;
  return request.id ? `document:${request.type}:${request.id}` : `document:${request.type}:new:${(seq += 1)}`;
}

/** Renders every open floating window (§1, §8.7, §11.1 — several at once, no page-blocking mask).
 *
 * §1.8/decision 9 «восстановление открытого окна после F5 через `?doc=<id>`»: the *document*
 * window currently open (or last opened, if several are) is mirrored into the URL's `doc` query
 * param — not category/payment/order-request windows, the plan names documents specifically, and
 * those other window kinds don't carry enough state in a bare id to make F5-restore meaningful the
 * same way (a payment/category window's id alone doesn't tell you its type). */
export function WindowManagerProvider({ children }: { children: ReactNode }) {
  const [windows, setWindows] = useState<OpenWindow[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [fetchDocument] = useLazyGetDocumentQuery();
  const restoreAttempted = useRef(false);

  const open = useCallback(
    (request: WindowRequest) => {
      const key = keyFor(request);
      setWindows((prev) => (prev.some((w) => w.key === key) ? prev : [...prev, { key, request }]));
      if (request.kind === 'document' && request.id) {
        const docId = request.id;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('doc', docId);
            return next;
          },
          { replace: true },
        );
      }
      return key;
    },
    [setSearchParams],
  );

  const close = useCallback(
    (key: string) => {
      setWindows((prev) => {
        const closedRequest = prev.find((w) => w.key === key)?.request;
        if (closedRequest?.kind === 'document' && closedRequest.id) {
          const docId = closedRequest.id;
          setSearchParams(
            (sp) => {
              if (sp.get('doc') !== docId) return sp;
              const next = new URLSearchParams(sp);
              next.delete('doc');
              return next;
            },
            { replace: true },
          );
        }
        return prev.filter((w) => w.key !== key);
      });
    },
    [setSearchParams],
  );

  const focus = useCallback(() => {
    // z-index ordering is handled inside FloatingWindow itself on pointer-down.
  }, []);

  const manager = useMemo(() => ({ windows, open, close, focus }), [windows, open, close, focus]);

  // Restore once per mount (i.e. once per full page load — this provider only remounts on F5,
  // never on in-app navigation between owner pages, since it wraps `<Outlet/>` inside the layout
  // that stays mounted across them). Fetches the document just to learn its `type` — the window
  // itself re-fetches the full document normally once opened.
  useEffect(() => {
    if (restoreAttempted.current) return;
    restoreAttempted.current = true;
    const docId = searchParams.get('doc');
    if (!docId) return;
    fetchDocument(docId)
      .unwrap()
      .then((doc) => {
        open({ kind: 'document', type: doc.type, id: doc.id });
      })
      .catch(() => {
        // Deleted/inaccessible — drop the stale param instead of leaving a dead link in the URL.
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('doc');
            return next;
          },
          { replace: true },
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs exactly once, deliberately not re-running on dep changes
  }, []);

  return (
    <Provider value={manager}>
      {children}
      {windows.map(({ key, request }) => {
        if (request.kind === 'category') {
          return <CategoryWindow key={key} id={request.id} parentId={request.parentId} onClose={() => close(key)} />;
        }
        if (request.kind === 'categoryGroup') {
          return <CategoryGroupWindow key={key} id={request.id} onClose={() => close(key)} />;
        }
        if (request.kind === 'categoryCreate') {
          return <CategoryCreateWindow key={key} parentId={request.parentId} onClose={() => close(key)} />;
        }
        if (request.kind === 'rootGroupCreate') {
          return <RootGroupCreateWindow key={key} onClose={() => close(key)} />;
        }
        if (request.kind === 'counterpartyCreate') {
          return <CounterpartyCreateWindow key={key} onClose={() => close(key)} />;
        }
        if (request.kind === 'payment') {
          return (
            <PaymentWindow
              key={key}
              id={request.id}
              counterpartyId={request.counterpartyId}
              prefillDocumentId={request.prefillDocumentId}
              prefillAmount={request.prefillAmount}
              direction={request.direction}
              onClose={() => close(key)}
            />
          );
        }
        if (request.kind === 'orderRequest') {
          return <OrderRequestWindow key={key} id={request.id} onClose={() => close(key)} />;
        }
        return (
          <DocumentWindow
            key={key}
            type={request.type}
            id={request.id}
            copyOf={request.copyOf}
            copyOfLabel={request.copyOfLabel}
            returnTemplateFrom={request.returnTemplateFrom}
            onClose={() => close(key)}
          />
        );
      })}
    </Provider>
  );
}
